import React,{useState} from 'react';
import {useNavigate} from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function DocumentsPage({documents,deleteDocument,onLoadDocument}) {
  const navigate=useNavigate();
  const [isDownloading,setIsDownloading]=useState(false);
  const [downloadMessage,setDownloadMessage]=useState('');

  const showDownloadNotification=(message)=> {
    setIsDownloading(true);
    setDownloadMessage(message);
    // Ocultar la notificación después de 3 segundos
    setTimeout(()=> {
      setIsDownloading(false);
      setDownloadMessage('');
    },3000);
  };

  const handleDownloadSingle=(doc)=> {
    // Validar que el nombre del cliente esté configurado
    if (!doc.config?.clientName || doc.config.clientName.trim()==='') {
      alert('No se puede generar el PDF porque el nombre del cliente no está configurado. Por favor,configure el cliente en la sección Configuración.');
      return;
    }
    
    showDownloadNotification('Iniciando descarga del PDF...');
    setTimeout(()=> {
      generatePDF(doc);
    },100);
  };

  const handleDownloadAll=async ()=> {
    if (documents.length===0) {
      alert('No hay documentos para descargar');
      return;
    }

    // Validar que todos los documentos tengan el nombre del cliente configurado
    const invalidDocs=documents.filter(doc=> !doc.config?.clientName || doc.config.clientName.trim()==='');
    if (invalidDocs.length > 0) {
      alert(`No se pueden generar ${invalidDocs.length} documento(s) porque el nombre del cliente no está configurado. Por favor,configure el cliente en la sección Configuración.`);
      return;
    }
    
    showDownloadNotification('Iniciando descarga de todos los PDFs...');
    setTimeout(async ()=> {
      try {
        await generateAllPDFs(documents);
      } catch (error) {
        console.error('Error al generar PDFs:',error);
        alert('Error al generar los PDFs. Por favor,inténtalo de nuevo.');
      }
    },100);
  };

  const handleDownloadCSV=()=> {
    if (documents.length===0) {
      alert('No hay documentos para exportar');
      return;
    }

    // Validar que todos los documentos tengan el nombre del cliente configurado
    const invalidDocs=documents.filter(doc=> !doc.config?.clientName || doc.config.clientName.trim()==='');
    if (invalidDocs.length > 0) {
      alert(`No se pueden exportar ${invalidDocs.length} documento(s) porque el nombre del cliente no está configurado. Por favor,configure el cliente en la sección Configuración.`);
      return;
    }
    generateCSV(documents);
  };

  const handleEdit=(doc)=> {
    if (onLoadDocument) {
      onLoadDocument(doc);
      navigate('/');
    }
  };

  // Función para limpiar texto para CSV (sin tildes ni caracteres especiales)
  const cleanTextForCSV=(text)=> {
    if (!text) return '';

    // Convertir a string y normalizar
    const str=String(text);

    // Remover tildes y caracteres especiales
    const normalized=str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g,'') // Remover diacríticos
      .replace(/[^\w\s-]/g,'') // Solo letras,números,espacios y guiones
      .replace(/\s+/g,' ') // Normalizar espacios
      .trim();

    return normalized;
  };

  // Función para escapar campos CSV específicamente para Excel
  const escapeCSVFieldForExcel=(field)=> {
    if (field===undefined || field===null) return '';

    // Asegurar que el campo sea una cadena de texto
    const cleanField=cleanTextForCSV(String(field));

    // Para Excel,siempre envolver campos que contengan espacios,comas o caracteres especiales
    if (cleanField.includes(',') || cleanField.includes('"') || cleanField.includes('\n') || cleanField.includes('\r') || cleanField.includes(' ')) {
      // Escapar comillas dobles duplicándolas y envolver en comillas
      return `"${cleanField.replace(/"/g,'""')}"`;
    }
    return cleanField;
  };

  const generateCSV=(docs)=> {
    try {
      console.log('📊 Iniciando generación de CSV optimizado para Excel con',docs.length,'documentos');

      // Verificar que todos los documentos están disponibles
      if (!Array.isArray(docs) || docs.length===0) {
        throw new Error('No hay documentos disponibles para exportar');
      }
      console.log('👨‍👩‍👧‍👦 Total de documentos a exportar:',docs.length);

      // Ordenar documentos por orden de ejecución (fecha de creación)
      const sortedDocs=[...docs].sort((a,b)=> {
        const dateA=new Date(a.createdAt || a.date || 0);
        const dateB=new Date(b.createdAt || b.date || 0);
        return dateA - dateB;// Orden ascendente (más antiguo primero)
      });
      console.log('📋 Documentos ordenados:',sortedDocs.length);

      // Obtener todas las prendas únicas de todos los documentos
      const allGarments=new Set();
      sortedDocs.forEach(doc=> {
        if (doc && doc.garments && typeof doc.garments==='object') {
          Object.keys(doc.garments).forEach(garment=> {
            if (garment && garment.trim() !=='') {
              allGarments.add(garment);
            }
          });
        } else {
          console.warn('⚠️ Documento sin prendas o formato inválido:',doc?.id || 'desconocido');
        }
      });

      const garmentArray=Array.from(allGarments).sort();
      console.log('👕 Prendas encontradas:',garmentArray.length,garmentArray);

      // Crear encabezados del CSV (optimizados para Excel)
      // REMOVIDO: 'Vestuario' ya que se usa 'Ubicacion' para el mismo propósito
      const headers=[
        'Nombre','Numero_Empleado','Grupo','Ubicacion','Taquilla','Fecha',
        ...garmentArray.map(g=> `Talla_${cleanTextForCSV(g)}`),
        'Largo_Pantalon_cm'
      ];
      console.log('📋 Encabezados CSV:',headers);

      // Crear filas de datos con verificación de cada documento
      const rows=sortedDocs.map((doc,index)=> {
        console.log(`📄 Procesando documento ${index + 1}/${sortedDocs.length}:`,doc?.name || 'Sin nombre');
        if (!doc) {
          console.warn(`⚠️ Documento ${index + 1} es null o undefined`);
          return Array(headers.length).fill('');
        }

        // Valores básicos con validación
        // REMOVIDO: doc.vestuario ya que se usa doc.location para el mismo propósito
        const row=[
          escapeCSVFieldForExcel(doc.name || ''),
          escapeCSVFieldForExcel(doc.employeeNumber || ''),
          escapeCSVFieldForExcel(doc.group || ''),
          escapeCSVFieldForExcel(doc.location || ''),
          escapeCSVFieldForExcel(doc.lockerNumber || ''),
          escapeCSVFieldForExcel(doc.date || '')
        ];

        // Añadir tallas para cada prenda (garantizando que existan)
        garmentArray.forEach(garment=> {
          const size=doc.garments && doc.garments[garment] ? doc.garments[garment] : '';
          row.push(escapeCSVFieldForExcel(size));
        });

        // Añadir largo de pantalón
        row.push(escapeCSVFieldForExcel(doc.pantsLength || ''));

        return row;
      });
      console.log(`✅ Procesadas ${rows.length} filas de datos`);

      if (rows.length !==sortedDocs.length) {
        console.error(`⚠️ Error: Se procesaron ${rows.length} filas pero había ${sortedDocs.length} documentos`);
      }

      // Detectar configuración regional para el separador
      const userLocale=navigator.language || 'es-ES';
      const isSpanishLocale=userLocale.startsWith('es') || userLocale.startsWith('ca');

      // En España y países de habla hispana,Excel usa punto y coma como separador
      const separator=isSpanishLocale ? ';' : ',';
      console.log('🌍 Locale detectado:',userLocale,'- Separador:',separator);

      // Convertir a CSV con el separador apropiado
      const csvContent=[
        headers.join(separator),
        ...rows.map(row=> row.join(separator))
      ].join('\r\n');// Usar \r\n para mejor compatibilidad con Excel

      console.log('📝 CSV generado con separador',separator,'- longitud:',csvContent.length);
      console.log('📝 Primeras líneas del CSV:');
      console.log(csvContent.split('\r\n').slice(0,Math.min(3,rows.length + 1)).join('\r\n'));
      console.log('📝 Total de filas en CSV (incluyendo encabezados):',csvContent.split('\r\n').length);

      // Crear blob con BOM UTF-8 y tipo MIME específico para Excel
      const BOM='\uFEFF';
      const mimeType=isSpanishLocale ? 'text/csv;charset=utf-8' : 'application/vnd.ms-excel;charset=utf-8';
      const blob=new Blob([BOM + csvContent],{type: mimeType});

      // Crear enlace de descarga
      const link=document.createElement('a');
      const url=URL.createObjectURL(blob);

      // Generar nombre de archivo limpio
      const clientName=docs[0]?.config?.clientName || 'Cliente';
      const today=new Date().toISOString().split('T')[0];
      const fileName=`Usuarios_Uniformes_${cleanTextForCSV(clientName)}_${today}.csv`;
      console.log('💾 Descargando archivo:',fileName);

      link.setAttribute('href',url);
      link.setAttribute('download',fileName);
      link.style.visibility='hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL
      setTimeout(()=> {
        URL.revokeObjectURL(url);
      },1000);

      const separatorName=separator===';' ? 'punto y coma (;)' : 'coma (,)';
      alert(`✅ CSV exportado exitosamente con ${sortedDocs.length} usuarios
📊 Archivo: ${fileName}

🔧 Optimizado para Excel:
  • Separador: ${separatorName}
  • Codificación: UTF-8 con BOM
  • Campos entrecomillados automáticamente

📋 Datos incluidos:
  • Información personal de ${sortedDocs.length} usuarios
  • Tallas de ${garmentArray.length} prendas diferentes
  • Largo de pantalón en centímetros

💡 El archivo se abrirá automáticamente con columnas separadas en Microsoft Excel
${isSpanishLocale ? '🇪🇸 Configurado para región española (separador ;)' : '🌍 Configurado para región internacional (separador ,)'}`);
    } catch (error) {
      console.error('❌ Error al generar CSV:',error);
      alert('Error al generar el CSV. Por favor,inténtalo de nuevo.\n\nDetalle del error: ' + error.message);
    }
  };

  // Función optimizada para comprimir imagen
  const compressImage=(src,maxWidth=800,quality=0.8)=> {
    return new Promise((resolve)=> {
      const img=new Image();
      img.crossOrigin='anonymous';
      img.onload=()=> {
        const canvas=document.createElement('canvas');
        const ctx=canvas.getContext('2d');
        
        // Calcular nuevas dimensiones manteniendo la proporción
        const ratio=Math.min(maxWidth / img.width,1);
        const width=Math.floor(img.width * ratio);
        const height=Math.floor(img.height * ratio);
        
        canvas.width=width;
        canvas.height=height;
        
        // Dibujar imagen con filtro para mejorar la compresión
        ctx.imageSmoothingEnabled=true;
        ctx.imageSmoothingQuality='high';
        ctx.drawImage(img,0,0,width,height);
        
        // Convertir a data URL con calidad reducida
        const dataURL=canvas.toDataURL('image/jpeg',quality);
        resolve(dataURL);
      };
      img.src=src;
    });
  };

  // Función para extraer firma como imagen optimizada
  const extractSignatureAsImage=async (signatureData,maxWidth=300)=> {
    if (!signatureData) return '';
    
    try {
      // Si ya es una URL de datos,comprimirla
      if (signatureData.startsWith('data:image')) {
        return await compressImage(signatureData,maxWidth,0.7);
      }
      return signatureData;
    } catch (error) {
      console.warn('No se pudo comprimir la firma:',error);
      return signatureData;
    }
  };

  // Función para generar un solo PDF optimizado y descargarlo directamente
  const generatePDF=async (doc)=> {
    try {
      // Crear un elemento temporal para renderizar el contenido del PDF
      const pdfContent=document.createElement('div');
      pdfContent.style.position='absolute';
      pdfContent.style.left='-9999px';
      pdfContent.style.width='210mm';// Ancho A4
      pdfContent.style.minHeight='297mm';// Alto A4
      pdfContent.style.padding='0mm';// Sin padding para evitar márgenes
      pdfContent.style.backgroundColor='white';
      pdfContent.style.fontFamily='Arial,Helvetica,sans-serif';
      pdfContent.style.fontSize='10px';
      pdfContent.style.lineHeight='1.2';
      
      // Obtener la configuración del documento
      const config=doc.config;
      const selectedMeasurer=(config.measurers || []).find(m=> m.id===doc.selectedMeasurerId) || (config.measurers || [])[0];
      
      // Comprimir firmas si existen
      const compressedMeasurerSignature=await extractSignatureAsImage(selectedMeasurer?.signature,200);
      const compressedInterestedSignature=await extractSignatureAsImage(doc.interestedSignature,200);
      
      // Construir el contenido HTML del documento
      pdfContent.innerHTML=`
        <div style="width: 100%;">
          <!-- Encabezado con logos sin márgenes -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin: 0; padding: 10mm 10mm 5mm 10mm; border-bottom: 2px solid #3b82f6;">
            <div style="flex: 0 0 25%; display: flex; justify-content: flex-start;">
              <img src="https://assets.brandbay.io/brands/1736547941865x394131539339247600/assets/Y7TrUoiturri%20horizontal%20sub.svg" alt="Iturri Logo" style="max-height: 50px; max-width: 100%; object-fit: contain;" />
            </div>
            <div style="flex: 1; text-align: center; padding: 0 10px;">
              ${config.clientLogo ? `<img src="${config.clientLogo}" alt="Client Logo" style="max-height: 40px; max-width: 100%; object-fit: contain; margin-bottom: 5px;" />` : config.clientName ? `<h2 style="font-size: 12px; font-weight: bold; margin-bottom: 5px; color: #3b82f6; text-transform: uppercase;">${config.clientName}</h2>` : ''}
            </div>
            <div style="flex: 0 0 25%; display: flex; justify-content: flex-end;">
              <img src="https://assets.brandbay.io/brands/1736547941865x394131539339247600/assets/67wPTbSello%20Iturri.jpg" alt="Sello Iturri" style="max-height: 50px; max-width: 100%; object-fit: contain;" />
            </div>
          </div>
          
          <!-- Sección de datos del usuario -->
          <div style="margin: 0; padding: 10mm;">
            <div style="background-color: #fef3c7; padding: 8px 12px; font-weight: bold; text-align: center; border: 2px solid #3b82f6; margin-bottom: 10px; font-size: 12px; letter-spacing: 0.5px; border-radius: 4px; box-shadow: 0 1px 2px rgba(59,130,246,0.1); color: #1e40af; text-transform: uppercase;">
              DATOS DEL USUARIO
            </div>
            <div style="margin-bottom: 10px;">
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px;">
                <div>
                  <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">NOMBRE:</label>
                  <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                    ${doc.name}
                  </div>
                </div>
                <div>
                  <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">Nº EMPLEADO:</label>
                  <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                    ${doc.employeeNumber || ''}
                  </div>
                </div>
              </div>
            </div>
            <div style="margin-bottom: 10px;">
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px;">
                <div>
                  <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">GRUPO:</label>
                  <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                    ${doc.group}
                  </div>
                </div>
                <div>
                  <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">UBICACIÓN:</label>
                  <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                    ${doc.location || ''}
                  </div>
                </div>
                <div>
                  <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">TAQUILLA:</label>
                  <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                    ${doc.lockerNumber || ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Sección de datos de las tallas -->
          <div style="margin: 0; padding: 0 10mm 10mm 10mm;">
            <div style="background-color: #fef3c7; padding: 8px 12px; font-weight: bold; text-align: center; border: 2px solid #3b82f6; margin-bottom: 10px; font-size: 12px; letter-spacing: 0.5px; border-radius: 4px; box-shadow: 0 1px 2px rgba(59,130,246,0.1); color: #1e40af; text-transform: uppercase;">
              DATOS DE LAS TALLAS
            </div>
            <table style="width: 100%; border-collapse: collapse; border: 2px solid #3b82f6; margin-bottom: 10px; border-radius: 4px; overflow: hidden;">
              <thead>
                <tr>
                  <th style="width: 70%; border: 1px solid #3b82f6; padding: 8px; text-align: left; background-color: #dbeafe; font-weight: bold; color: #1e40af; font-size: 11px; text-transform: uppercase;">VESTUARIO</th>
                  <th style="width: 30%; border: 1px solid #3b82f6; padding: 8px; text-align: left; background-color: #dbeafe; font-weight: bold; color: #1e40af; font-size: 11px; text-transform: uppercase;">TALLA</th>
                </tr>
              </thead>
              <tbody>
                ${config.garmentTypes.map(garment=> {
                  const garmentName=typeof garment==='string' ? garment : garment.name;
                  const size=doc.garments[garmentName] || '';
                  return `
                  <tr>
                    <td style="border: 1px solid #3b82f6; padding: 8px; text-align: left; font-size: 10px; background-color: #f8fafc; text-transform: uppercase;">${garmentName}</td>
                    <td style="border: 1px solid #3b82f6; padding: 8px; text-align: center; background-color: white;">
                      <div style="display: flex; justify-content: center; align-items: center; height: 100%;">
                        <div style="border: 1px solid #3b82f6; padding: 4px 8px; border-radius: 3px; font-weight: 500; min-width: 60px; text-align: center; text-transform: uppercase; display: flex; align-items: center; justify-content: center; min-height: 24px;">
                          ${size}
                        </div>
                      </div>
                    </td>
                  </tr>
                `;
                }).join('')}
              </tbody>
            </table>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; padding: 10px; border: 2px solid #3b82f6; border-radius: 4px; background-color: #f8fafc;">
              <div>
                <label style="font-weight: bold; color: #1e40af; font-size: 11px; text-transform: uppercase;">LARGO PANTALÓN (EN CM.):</label>
              </div>
              <div>
                <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; font-weight: 500; background-color: white; text-transform: uppercase; display: flex; align-items: center; min-height: 24px;">
                  ${doc.pantsLength}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Sección de firmas -->
          <div style="margin: 0; padding: 0 10mm 10mm 10mm;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div style="border: 2px solid #3b82f6; padding: 12px; border-radius: 6px; background-color: #f8fafc;">
                <h4 style="font-weight: bold; margin-bottom: 10px; font-size: 12px; color: #1e40af; text-align: center; text-transform: uppercase;">NOMBRE Y FIRMA MEDIDOR</h4>
                <div style="margin-bottom: 10px; padding: 6px; background-color: #fef3c7; border: 1px solid #3b82f6; border-radius: 3px; font-size: 10px; font-weight: 500; text-align: center; text-transform: uppercase; min-height: 24px; display: flex; align-items: center; justify-content: center;">
                  ${selectedMeasurer?.name || ''}
                </div>
                <div style="height: 50px; border: 1px solid #3b82f6; background-color: white; display: flex; align-items: center; justify-content: center; position: relative; border-radius: 3px;">
                  ${compressedMeasurerSignature ? `<img src="${compressedMeasurerSignature}" alt="Firma del medidor" style="max-height: 45px; max-width: 100%; object-fit: contain;" />` : '<div style="color: #999; font-size: 9px; text-align: center;">Firma del medidor</div>'}
                </div>
              </div>
              <div style="border: 2px solid #3b82f6; padding: 12px; border-radius: 6px; background-color: #f8fafc;">
                <h4 style="font-weight: bold; margin-bottom: 10px; font-size: 12px; color: #1e40af; text-align: center; text-transform: uppercase;">NOMBRE Y FIRMA DEL INTERESADO</h4>
                <div style="margin-bottom: 10px; padding: 6px; background-color: #fef3c7; border: 1px solid #3b82f6; border-radius: 3px; font-size: 10px; font-weight: 500; text-align: center; text-transform: uppercase; min-height: 24px; display: flex; align-items: center; justify-content: center;">
                  ${doc.interestedName}
                </div>
                <div style="height: 50px; border: 1px solid #3b82f6; background-color: white; display: flex; align-items: center; justify-content: center; position: relative; border-radius: 3px;">
                  ${compressedInterestedSignature ? `<img src="${compressedInterestedSignature}" alt="Firma del interesado" style="max-height: 45px; max-width: 100%; object-fit: contain;" />` : '<div style="color: #999; font-size: 9px; text-align: center;">Firma del interesado</div>'}
                </div>
              </div>
            </div>
            <div style="margin-bottom: 10px;">
              <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">FECHA:</label>
              <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                ${doc.date}
              </div>
            </div>
          </div>
          
          <!-- Pie de página con información legal -->
          <div style="font-size: 7px; line-height: 1.2; margin: 0; padding: 10mm; border-top: 2px solid #3b82f6; text-align: justify; background-color: #f8fafc; border-radius: 0 0 4px 4px; text-transform: uppercase;">
            <strong style="display: block; margin-bottom: 3px; font-size: 8px; color: #1e40af; text-transform: uppercase;">INFORMACIÓN SOBRE PROTECCIÓN DE DATOS:</strong>
            De conformidad con lo previsto en el artículo 5 de la Ley Orgánica 15/1999 de 13 de Diciembre,se informa que los datos recabados en el siguiente formulario serán incorporados en un fichero titularidad del cliente cuya finalidad es la gestión del suministro de uniformes de empleados. El titular de los datos consiente expresamente que sus datos sean trasladados para la finalidad entradicha,pudiendo ejercitar los derechos de acceso,rectificación,cancelación y oposición en los términos previstos en la Ley Orgánica 15/1999,dirigido escrito a [insertar denominación dpto. y dirección]. Los datos señalados con asteríscos son de carácter voluntario.
          </div>
        </div>
      `;
      
      // Añadir el elemento al DOM
      document.body.appendChild(pdfContent);
      
      // Usar html2canvas para capturar el contenido con configuración optimizada
      const canvas=await html2canvas(pdfContent,{
        scale: 1.2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 15000
      });
      
      // Crear PDF usando jsPDF con compresión
      const imgData=canvas.toDataURL('image/jpeg',0.7);// Comprimir a JPEG con calidad 70%
      const pdf=new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Agregar imagen al PDF con compresión
      pdf.addImage(imgData,'JPEG',0,0,210,297,undefined,'MEDIUM');
      
      // Generar nombre de archivo
      const clientName=doc.config?.clientName || 'Cliente';
      const today=new Date().toISOString().split('T')[0];
      const fileName=`Tallaje_${removeAccents(clientName)}_${today}_${doc.name.replace(/\s+/g,'_')}.pdf`;
      
      // Descargar el PDF
      pdf.save(fileName);
      
      // Remover el elemento temporal
      document.body.removeChild(pdfContent);
    } catch (error) {
      console.error('Error al generar el PDF:',error);
      alert('Error al generar el PDF. Por favor,inténtalo de nuevo.');
    }
  };

  // Función para generar todos los PDFs como un solo archivo optimizado y descargarlo
  const generateAllPDFs=async (docs)=> {
    try {
      // Obtener el nombre del primer documento para el nombre del archivo
      const clientName=docs[0]?.config?.clientName || 'Cliente';
      const today=new Date().toISOString().split('T')[0];
      const fileName=`Tallaje_${removeAccents(clientName)}_${today}.pdf`;
      
      // Mostrar notificación de progreso para grandes cantidades de documentos
      if (docs.length > 10) {
        showDownloadNotification(`Generando PDF con ${docs.length} documentos...`);
      }
      
      // Ordenar documentos por orden de ejecución (fecha de creación)
      const sortedDocs=[...docs].sort((a,b)=> {
        const dateA=new Date(a.createdAt || a.date);
        const dateB=new Date(b.createdAt || b.date);
        return dateA - dateB;// Orden ascendente (más antiguo primero)
      });
      
      // Crear PDF usando jsPDF con compresión
      const pdf=new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Procesar cada documento con manejo de errores individual
      for (let i=0;i < sortedDocs.length;i++) {
        try {
          const doc=sortedDocs[i];
          const config=doc.config;
          const selectedMeasurer=(config.measurers || []).find(m=> m.id===doc.selectedMeasurerId) || (config.measurers || [])[0];
          
          // Actualizar notificación de progreso para grandes cantidades
          if (docs.length > 10 && i % 5 === 0) {
            showDownloadNotification(`Generando PDF ${i+1}/${docs.length}...`);
          }
          
          // Crear un elemento temporal para renderizar el contenido del PDF
          const pdfContent=document.createElement('div');
          pdfContent.style.position='absolute';
          pdfContent.style.left='-9999px';
          pdfContent.style.width='210mm';// Ancho A4
          pdfContent.style.minHeight='297mm';// Alto A4
          pdfContent.style.padding='0mm';// Sin padding para evitar márgenes
          pdfContent.style.backgroundColor='white';
          pdfContent.style.fontFamily='Arial,Helvetica,sans-serif';
          pdfContent.style.fontSize='10px';
          pdfContent.style.lineHeight='1.2';
          
          // Comprimir firmas si existen
          const compressedMeasurerSignature=await extractSignatureAsImage(selectedMeasurer?.signature,200);
          const compressedInterestedSignature=await extractSignatureAsImage(doc.interestedSignature,200);
          
          // Construir el contenido HTML del documento
          pdfContent.innerHTML=`
            <div style="width: 100%;">
              <!-- Encabezado con logos sin márgenes -->
              <div style="display: flex; justify-content: space-between; align-items: center; margin: 0; padding: 10mm 10mm 5mm 10mm; border-bottom: 2px solid #3b82f6;">
                <div style="flex: 0 0 25%; display: flex; justify-content: flex-start;">
                  <img src="https://assets.brandbay.io/brands/1736547941865x394131539339247600/assets/Y7TrUoiturri%20horizontal%20sub.svg" alt="Iturri Logo" style="max-height: 50px; max-width: 100%; object-fit: contain;" />
                </div>
                <div style="flex: 1; text-align: center; padding: 0 10px;">
                  ${config.clientLogo ? `<img src="${config.clientLogo}" alt="Client Logo" style="max-height: 40px; max-width: 100%; object-fit: contain; margin-bottom: 5px;" />` : config.clientName ? `<h2 style="font-size: 12px; font-weight: bold; margin-bottom: 5px; color: #3b82f6; text-transform: uppercase;">${config.clientName}</h2>` : ''}
                </div>
                <div style="flex: 0 0 25%; display: flex; justify-content: flex-end;">
                  <img src="https://assets.brandbay.io/brands/1736547941865x394131539339247600/assets/67wPTbSello%20Iturri.jpg" alt="Sello Iturri" style="max-height: 50px; max-width: 100%; object-fit: contain;" />
                </div>
              </div>
              
              <!-- Sección de datos del usuario -->
              <div style="margin: 0; padding: 10mm;">
                <div style="background-color: #fef3c7; padding: 8px 12px; font-weight: bold; text-align: center; border: 2px solid #3b82f6; margin-bottom: 10px; font-size: 12px; letter-spacing: 0.5px; border-radius: 4px; box-shadow: 0 1px 2px rgba(59,130,246,0.1); color: #1e40af; text-transform: uppercase;">
                  DATOS DEL USUARIO
                </div>
                <div style="margin-bottom: 10px;">
                  <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px;">
                    <div>
                      <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">NOMBRE:</label>
                      <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                        ${doc.name}
                      </div>
                    </div>
                    <div>
                      <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">Nº EMPLEADO:</label>
                      <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                        ${doc.employeeNumber || ''}
                      </div>
                    </div>
                  </div>
                </div>
                <div style="margin-bottom: 10px;">
                  <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px;">
                    <div>
                      <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">GRUPO:</label>
                      <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                        ${doc.group}
                      </div>
                    </div>
                    <div>
                      <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">UBICACIÓN:</label>
                      <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                        ${doc.location || ''}
                      </div>
                    </div>
                    <div>
                      <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">TAQUILLA:</label>
                      <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                        ${doc.lockerNumber || ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Sección de datos de las tallas -->
              <div style="margin: 0; padding: 0 10mm 10mm 10mm;">
                <div style="background-color: #fef3c7; padding: 8px 12px; font-weight: bold; text-align: center; border: 2px solid #3b82f6; margin-bottom: 10px; font-size: 12px; letter-spacing: 0.5px; border-radius: 4px; box-shadow: 0 1px 2px rgba(59,130,246,0.1); color: #1e40af; text-transform: uppercase;">
                  DATOS DE LAS TALLAS
                </div>
                <table style="width: 100%; border-collapse: collapse; border: 2px solid #3b82f6; margin-bottom: 10px; border-radius: 4px; overflow: hidden;">
                  <thead>
                    <tr>
                      <th style="width: 70%; border: 1px solid #3b82f6; padding: 8px; text-align: left; background-color: #dbeafe; font-weight: bold; color: #1e40af; font-size: 11px; text-transform: uppercase;">VESTUARIO</th>
                      <th style="width: 30%; border: 1px solid #3b82f6; padding: 8px; text-align: left; background-color: #dbeafe; font-weight: bold; color: #1e40af; font-size: 11px; text-transform: uppercase;">TALLA</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${config.garmentTypes.map(garment=> {
                      const garmentName=typeof garment==='string' ? garment : garment.name;
                      const size=doc.garments[garmentName] || '';
                      return `
                      <tr>
                        <td style="border: 1px solid #3b82f6; padding: 8px; text-align: left; font-size: 10px; background-color: #f8fafc; text-transform: uppercase;">${garmentName}</td>
                        <td style="border: 1px solid #3b82f6; padding: 8px; text-align: center; background-color: white;">
                          <div style="display: flex; justify-content: center; align-items: center; height: 100%;">
                            <div style="border: 1px solid #3b82f6; padding: 4px 8px; border-radius: 3px; font-weight: 500; min-width: 60px; text-align: center; text-transform: uppercase; display: flex; align-items: center; justify-content: center; min-height: 24px;">
                              ${size}
                            </div>
                          </div>
                        </td>
                      </tr>
                    `;
                    }).join('')}
                  </tbody>
                </table>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; padding: 10px; border: 2px solid #3b82f6; border-radius: 4px; background-color: #f8fafc;">
                  <div>
                    <label style="font-weight: bold; color: #1e40af; font-size: 11px; text-transform: uppercase;">LARGO PANTALÓN (EN CM.):</label>
                  </div>
                  <div>
                    <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; font-weight: 500; background-color: white; text-transform: uppercase; display: flex; align-items: center; min-height: 24px;">
                      ${doc.pantsLength}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Sección de firmas -->
              <div style="margin: 0; padding: 0 10mm 10mm 10mm;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
                  <div style="border: 2px solid #3b82f6; padding: 12px; border-radius: 6px; background-color: #f8fafc;">
                    <h4 style="font-weight: bold; margin-bottom: 10px; font-size: 12px; color: #1e40af; text-align: center; text-transform: uppercase;">NOMBRE Y FIRMA MEDIDOR</h4>
                    <div style="margin-bottom: 10px; padding: 6px; background-color: #fef3c7; border: 1px solid #3b82f6; border-radius: 3px; font-size: 10px; font-weight: 500; text-align: center; text-transform: uppercase; min-height: 24px; display: flex; align-items: center; justify-content: center;">
                      ${selectedMeasurer?.name || ''}
                    </div>
                    <div style="height: 50px; border: 1px solid #3b82f6; background-color: white; display: flex; align-items: center; justify-content: center; position: relative; border-radius: 3px;">
                      ${compressedMeasurerSignature ? `<img src="${compressedMeasurerSignature}" alt="Firma del medidor" style="max-height: 45px; max-width: 100%; object-fit: contain;" />` : '<div style="color: #999; font-size: 9px; text-align: center;">Firma del medidor</div>'}
                    </div>
                  </div>
                  <div style="border: 2px solid #3b82f6; padding: 12px; border-radius: 6px; background-color: #f8fafc;">
                    <h4 style="font-weight: bold; margin-bottom: 10px; font-size: 12px; color: #1e40af; text-align: center; text-transform: uppercase;">NOMBRE Y FIRMA DEL INTERESADO</h4>
                    <div style="margin-bottom: 10px; padding: 6px; background-color: #fef3c7; border: 1px solid #3b82f6; border-radius: 3px; font-size: 10px; font-weight: 500; text-align: center; text-transform: uppercase; min-height: 24px; display: flex; align-items: center; justify-content: center;">
                      ${doc.interestedName}
                    </div>
                    <div style="height: 50px; border: 1px solid #3b82f6; background-color: white; display: flex; align-items: center; justify-content: center; position: relative; border-radius: 3px;">
                      ${compressedInterestedSignature ? `<img src="${compressedInterestedSignature}" alt="Firma del interesado" style="max-height: 45px; max-width: 100%; object-fit: contain;" />` : '<div style="color: #999; font-size: 9px; text-align: center;">Firma del interesado</div>'}
                    </div>
                  </div>
                </div>
                <div style="margin-bottom: 10px;">
                  <label style="font-weight: bold; display: block; margin-bottom: 3px; color: #1e40af; text-transform: uppercase;">FECHA:</label>
                  <div style="border: 1px solid #3b82f6; padding: 6px 10px; border-radius: 3px; background-color: #f8fafc; font-weight: 500; text-transform: uppercase; display: flex; align-items: center; min-height: 30px;">
                    ${doc.date}
                  </div>
                </div>
              </div>
              
              <!-- Pie de página con información legal -->
              <div style="font-size: 7px; line-height: 1.2; margin: 0; padding: 10mm; border-top: 2px solid #3b82f6; text-align: justify; background-color: #f8fafc; border-radius: 0 0 4px 4px; text-transform: uppercase;">
                <strong style="display: block; margin-bottom: 3px; font-size: 8px; color: #1e40af; text-transform: uppercase;">INFORMACIÓN SOBRE PROTECCIÓN DE DATOS:</strong>
                De conformidad con lo previsto en el artículo 5 de la Ley Orgánica 15/1999 de 13 de Diciembre,se informa que los datos recabados en el siguiente formulario serán incorporados en un fichero titularidad del cliente cuya finalidad es la gestión del suministro de uniformes de empleados. El titular de los datos consiente expresamente que sus datos sean trasladados para la finalidad entradicha,pudiendo ejercitar los derechos de acceso,rectificación,cancelación y oposición en los términos previstos en la Ley Orgánica 15/1999,dirigido escrito a [insertar denominación dpto. y dirección]. Los datos señalados con asteríscos son de carácter voluntario.
              </div>
            </div>
          `;
          
          // Añadir el elemento al DOM
          document.body.appendChild(pdfContent);
          
          // Usar html2canvas para capturar el contenido con configuración optimizada
          const canvas=await html2canvas(pdfContent,{
            scale: 1.2,
            useCORS: true,
            logging: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            imageTimeout: 15000
          });
          
          // Calcular dimensiones para ajustar al PDF
          const imgData=canvas.toDataURL('image/jpeg',0.7);// Comprimir a JPEG con calidad 70%
          const imgWidth=210;// Ancho A4 en mm
          const pageHeight=297;// Alto A4 en mm
          const imgHeight=(canvas.height * imgWidth) / canvas.width;
          let heightLeft=imgHeight;
          let position=0;
          
          // Si no es el primer documento,agregar una nueva página
          if (i > 0) {
            pdf.addPage();
          }
          
          // Agregar la imagen al PDF con compresión
          pdf.addImage(imgData,'JPEG',0,position,imgWidth,imgHeight,undefined,'MEDIUM');
          heightLeft -=pageHeight;
          
          // Agregar páginas adicionales si es necesario
          while (heightLeft >=0) {
            position=heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData,'JPEG',0,position,imgWidth,imgHeight,undefined,'MEDIUM');
            heightLeft -=pageHeight;
          }
          
          // Remover el elemento temporal
          document.body.removeChild(pdfContent);
        } catch (docError) {
          console.error(`Error procesando documento ${i+1}:`,docError);
          // Continuar con el siguiente documento en caso de error
          continue;
        }
      }
      
      // Descargar el PDF
      pdf.save(fileName);
      
      // Mostrar mensaje de éxito
      if (docs.length > 10) {
        showDownloadNotification(`PDF generado con ${docs.length} documentos`);
      }
      
      return true;
    } catch (error) {
      console.error('Error al generar los PDFs:',error);
      throw error;
    }
  };

  // Función para eliminar tildes y caracteres especiales (para nombres de archivo)
  const removeAccents=(str)=> {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g,'');
  };

  const formatDate=(dateString)=> {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getGarmentName=(garment)=> {
    return typeof garment==='string' ? garment : garment.name;
  };

  const handleDelete=(id)=> {
    console.log('Intentando eliminar documento con ID:',id);
    // Debug
    console.log('Función deleteDocument:',typeof deleteDocument);
    // Debug
    if (window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
      try {
        deleteDocument(id);
        console.log('Documento eliminado exitosamente');
        // Debug
      } catch (error) {
        console.error('Error al eliminar documento:',error);
        // Debug
        alert('Error al eliminar el documento. Por favor,inténtalo de nuevo.');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
      {/* Notificación de descarga */}
      {isDownloading && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          <span>{downloadMessage}</span>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-0">Documentos</h1>
        {documents.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={handleDownloadAll}
              className="flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              Descargar Todos ({documents.length})
            </button>
            <button
              onClick={handleDownloadCSV}
              className="flex items-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Descargar CSV
            </button>
          </div>
        )}
      </div>
      {documents.length===0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12 sm:h-16 sm:w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-500 text-sm sm:text-base">No hay documentos guardados</p>
          <p className="text-xs sm:text-sm text-gray-400 mt-2">
            Los documentos aparecerán aquí después de guardarlos desde el formulario
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc)=> {
            const selectedMeasurer=(doc.config.measurers || []).find(m=> m.id===doc.selectedMeasurerId) || (doc.config.measurers || [])[0];
            return (
              <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow bg-white relative">
                {/* Pestaña con iconos de acción */}
                <div className="absolute top-0 right-0 bg-white rounded-bl-lg shadow-md overflow-hidden">
                  <div className="flex">
                    <button
                      onClick={()=> handleDownloadSingle(doc)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors border-r border-gray-200"
                      title="Descargar PDF"
                      disabled={!doc.config?.clientName}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                    </button>
                    <button
                      onClick={()=> handleEdit(doc)}
                      className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 transition-colors border-r border-gray-200"
                      title="Editar documento"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={()=> handleDelete(doc.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                      title="Eliminar documento"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Contenido de la tarjeta */}
                <div className="pt-2">
                  <h3 className="font-semibold text-sm text-gray-900 mb-1">{doc.name}</h3>
                  {doc.group && (
                    <p className="text-xs text-gray-600 mb-2">{doc.group}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs mb-3">
                    {doc.employeeNumber && (
                      <span className="bg-gray-100 px-2 py-1 rounded">Nº: {doc.employeeNumber}</span>
                    )}
                    {doc.location && (
                      <span className="bg-gray-100 px-2 py-1 rounded">Ubicación: {doc.location}</span>
                    )}
                    {doc.lockerNumber && (
                      <span className="bg-gray-100 px-2 py-1 rounded">Taquilla: {doc.lockerNumber}</span>
                    )}
                  </div>
                  <div className="mb-3">
                    <div className="space-y-1">
                      {Object.entries(doc.garments).map(([garment,size])=> (
                        <div key={garment} className="flex justify-between text-xs">
                          <span className="text-gray-600">{getGarmentName(garment)}:</span>
                          <span className="font-medium">{size}</span>
                        </div>
                      ))}
                      {doc.pantsLength && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Largo pantalón:</span>
                          <span className="font-medium">{doc.pantsLength} cm</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 border-t border-gray-100 pt-2">
                    {formatDate(doc.date)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DocumentsPage;